import React, { useState, useRef, useEffect } from 'react'
import classNames from 'classnames/bind'
import { router } from '@inertiajs/react'
import { useAdminAuth } from '@/admin/shared/hooks/useAdminPageProps'
import styles from './AdminHeader.module.scss'

const cx = classNames.bind(styles)

type AdminTheme = 'light' | 'dark'

interface AdminHeaderProps {
  title: string
  theme: AdminTheme
  onThemeToggle: () => void
  onMenuOpen?: () => void
}

// ── Sun / Moon icons ──────────────────────────────────────────────────────────
const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/>
  </svg>
)

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
)

// ── Component ─────────────────────────────────────────────────────────────────

function AdminHeader({ title, theme, onThemeToggle, onMenuOpen }: AdminHeaderProps) {
  const [profileOpen, setProfileOpen] = useState(false)
  const profileRef = useRef<HTMLDivElement>(null)

  const { user } = useAdminAuth()
  const userName = user?.name ?? 'Admin'
  const userEmail = user?.email ?? ''
  const initials = userName
    .split(' ')
    .map((w: string) => w[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogout = async () => {
    setProfileOpen(false)
    await fetch('/api/app/user/logout', { method: 'POST' })
    router.visit('/login')
  }

  return (
    <header className={cx('topbar')}>
      {/* Left: hamburger + breadcrumbs */}
      <div className={cx('crumbs')}>
        <button
          className={cx('hamburger')}
          data-admin-hamburger
          onClick={onMenuOpen}
          aria-label="Open navigation"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
        <span className={cx('crumbSection')}>Admin</span>
        <svg className={cx('crumbSep')} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m9 18 6-6-6-6"/>
        </svg>
        <span className={cx('crumbCurrent')}>{title}</span>
      </div>

      {/* Right: actions */}
      <div className={cx('actions')}>
        {/* Theme toggle */}
        <button className={cx('iconBtn')} onClick={onThemeToggle} aria-label="Toggle theme" title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}>
          {theme === 'light' ? <MoonIcon /> : <SunIcon />}
        </button>

        {/* Profile dropdown */}
        <div className={cx('ddWrap', { ddOpen: profileOpen })} ref={profileRef}>
          <div
            className={cx('avatar')}
            onClick={() => setProfileOpen(v => !v)}
            role="button"
            tabIndex={0}
            aria-label="Account menu"
            aria-expanded={profileOpen}
            onKeyDown={e => e.key === 'Enter' && setProfileOpen(v => !v)}
          >
            {initials}
          </div>

          <div className={cx('ddMenu', 'ddProfile')} role="menu">
            <div className={cx('ddProfileHead')}>
              <div className={cx('ddProfileName')}>{userName}</div>
              {userEmail && <div className={cx('ddProfileEmail')}>{userEmail}</div>}
            </div>
            <button className={cx('ddMenuItem', 'ddMenuDanger')} onClick={handleLogout} role="menuitem">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
              </svg>
              Logout
            </button>
          </div>
        </div>
      </div>
    </header>
  )
}

export default AdminHeader
